import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsAPI } from '../services/api';
import useAuthStore from '../context/authStore';
import { hasUserModuleAccess, CLIENT_PORTAL_MODULES } from '../utils/clientPortalPermissions';

/**
 * Hook for client-side document approval actions
 * Only allows facility_admin and facility_manager roles to approve documents
 * facility_worker role cannot view or interact with documents
 */
export const useDocumentApprovals = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Check if user can approve documents
  const canApproveDocuments = () => {
    return hasUserModuleAccess(user, CLIENT_PORTAL_MODULES.consulting_document_approval, 'act');
  };

  // Approve document
  const approveMutation = useMutation({
    mutationFn: (documentId) => {
      if (!canApproveDocuments()) {
        throw new Error('You do not have permission to approve documents');
      }
      return documentsAPI.updateStatus(documentId, { status: 'approved' });
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  // Request revision
  const revisionMutation = useMutation({
    mutationFn: ({ documentId, comments }) => {
      if (!canApproveDocuments()) {
        throw new Error('You do not have permission to request revisions');
      }
      return documentsAPI.updateStatus(documentId, {
        status: 'revision',
        comments,
      });
    },
    onSuccess: (_, { documentId }) => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  // Deny document
  const denyMutation = useMutation({
    mutationFn: (documentId) => {
      if (!canApproveDocuments()) {
        throw new Error('You do not have permission to deny documents');
      }
      return documentsAPI.updateStatus(documentId, { status: 'denied' });
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['document', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });

  return {
    canApproveDocuments: canApproveDocuments(),
    approveMutation,
    revisionMutation,
    denyMutation,
    isApproving: approveMutation.isPending || revisionMutation.isPending || denyMutation.isPending,
  };
};
